import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Lottie from 'lottie-react';

import useAudioPlayer from '@/shared/hooks/useAudioPlayer';
import useResponsive from '@/shared/hooks/useResponsive';
import { EndOutcome } from '@/features/gameSession/types';
import { useLaneChooserTheme } from '@/features/laneChooserPhaser/theme';

import Button from '@/shared/components/button/Button';

import resultFailedSfx from '@/features/laneChooserPhaser/assets/sounds/result-failed.mp3';
import resultVictorySfx from '@/features/laneChooserPhaser/assets/sounds/result-victory.mp3';
import surfingSpeedwayIntro from '@/features/laneChooserPhaser/assets/sounds/surfing-speedway-intro.mp3';

import '@/features/laneChooserPhaser/styles/lane-chooser-end-screen.scss';

export type EndScreenProps = {
	className?: string;
	outcome: EndOutcome;
	onClose?: () => void;
};

const EndScreen = ({ className, outcome, onClose }: EndScreenProps) => {
	const { t } = useTranslation();
	const { isMobile, isTablet, isPortrait, isLandscape } = useResponsive();
	const theme = useLaneChooserTheme();

	const isSuccess = outcome === EndOutcome.SUCCEEDED;
	const isFailed = [EndOutcome.LOST, EndOutcome.ABANDONED].includes(outcome);
	const outcomeTranslationKey = isSuccess ? 'succeeded' : 'failed';
	const endScreenTranslationKey = `lane_chooser.end_screen.${theme.key}`;
	const endScreenTitle = t(`${endScreenTranslationKey}.title.${outcomeTranslationKey}`);
	const endScreenDescription = t(`${endScreenTranslationKey}.description.${outcomeTranslationKey}`);

	const { play: playEndMusic, stop: stopEndMusic } = useAudioPlayer({
		src: surfingSpeedwayIntro,
		loop: true,
		volume: 0.5,
	});
	const { play: playVictorySfx, stop: stopVictorySfx } = useAudioPlayer({
		src: resultVictorySfx,
		volume: 0.7,
	});
	const { play: playFailedSfx, stop: stopFailedSfx } = useAudioPlayer({
		src: resultFailedSfx,
		volume: 0.7,
	});

	const animationsFailed = theme.animations.endScreen.failed;
	const animationsSuccess = theme.animations.endScreen.success;

	const animationData = useMemo(() => {
		if (isSuccess) {
			return isMobile
				? isPortrait
					? animationsSuccess.mobile
					: animationsSuccess.tabletLandscape
				: isTablet
				? isPortrait
					? animationsSuccess.tabletPortrait
					: animationsSuccess.tabletLandscape
				: isLandscape
				? animationsSuccess.desktop
				: animationsSuccess.tabletPortrait;
		} else if (isFailed) {
			return isMobile
				? isPortrait
					? animationsFailed.mobile
					: animationsFailed.tabletLandscape
				: isTablet
				? isPortrait
					? animationsFailed.tabletPortrait
					: animationsFailed.tabletLandscape
				: isLandscape
				? animationsFailed.desktop
				: animationsFailed.tabletPortrait;
		}
		return null;
	}, [isMobile, isTablet, isPortrait, isLandscape, isSuccess, isFailed, animationsSuccess, animationsFailed]);

	const preserveAspectRatio = isLandscape ? 'xMidYMid slice' : 'xMaxYMid slice';

	useEffect(() => {
		playEndMusic();
		return () => {
			stopEndMusic();
		};
	}, [playEndMusic, stopEndMusic]);

	useEffect(() => {
		if (isSuccess) {
			playVictorySfx();
		} else {
			playFailedSfx();
		}
		return () => {
			stopVictorySfx();
			stopFailedSfx();
		};
	}, [isSuccess, playFailedSfx, playVictorySfx, stopFailedSfx, stopVictorySfx]);

	return (
		<div
			className={classNames(
				'lane-chooser-end-screen',
				`lane-chooser-end-screen--${theme.key}`,
				{
					'lane-chooser-end-screen--succeeded': isSuccess,
					'lane-chooser-end-screen--failed': isFailed,
				},
				className,
			)}
		>
			<div className="lane-chooser-end-screen__bg" aria-hidden="true">
				<Lottie animationData={animationData} loop autoplay rendererSettings={{ preserveAspectRatio }} />
			</div>
			<div className="lane-chooser-end-screen__inner">
				<h2 className="lane-chooser-end-screen__title">{endScreenTitle}</h2>
				<p className="lane-chooser-end-screen__description">{endScreenDescription}</p>
				<div className="lane-chooser-end-screen__actions">
					<Button
						type="primary"
						iconName="icon-arrow-circle-right"
						title={t('lane_chooser.end_screen.button.next')}
						onClick={onClose}
					/>
				</div>
			</div>
		</div>
	);
};

export default EndScreen;
